import { useEffect, useState, useMemo, useContext } from "react";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { TOContext } from "context/TContext";
import { useLocation } from "react-router-dom";
import { useApplyPaymentCardMutation } from "api";
import {
  useStripe,
  CardNumberElement,
  useElements,
} from "@stripe/react-stripe-js";
import { yupResolver } from "@hookform/resolvers/yup";
import { schemaCheckoutIn } from "hooks/useCreditCard";
import {
  PAYMENT_SYSTEM_TYPES,
  PaymentSystemContext,
} from "context/PaymentSystemContext";

export const defaultValues = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  acceptUpdatesPromotionEmails: true,
};

const useStripePay = () => {
  const { paymentSystem } = useContext(PaymentSystemContext);

  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const oId = searchParams.get("oId");
  const [isLoading, setIsLoading] = useState(false);

  const [applyPaymentCard] = useApplyPaymentCardMutation();

  const { business, charges, setIsPaid, paidTip } = useContext(TOContext);

  const [paymentRequest, setPaymentRequest] = useState(null);
  const [isStripeApplePaySupported, setIsStripeApplePaySupported] =
    useState(false);
  const [isStripeGooglePaySupported, setIsStripeGooglePaySupported] =
    useState(false);

  const userInfo = useForm({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(schemaCheckoutIn),
  });

  const isStripePayment = useMemo(() => {
    return paymentSystem === PAYMENT_SYSTEM_TYPES.STRIPE;
  }, [paymentSystem]);

  // Effect A — create the paymentRequest once when Stripe is ready and there is a balance due.
  // We intentionally omit `paymentRequest` from the deps so that updating the amount
  // (Effect B below) does not re-trigger creation.
  useEffect(() => {
    if (!isStripePayment || !stripe || !(charges?.balanceDue > 0) || paymentRequest) return;

    try {
      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: business?.title || business?.companyName || "Order Payment",
          amount: Math.round(charges.balanceDue * 100),
        },
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
      });

      pr.canMakePayment()
        .then((result) => {
          if (result) {
            const canUseGooglePay = result?.googlePay;
            const canUseApplePay = result?.applePay;
            if (canUseGooglePay || canUseApplePay) {
              setPaymentRequest(pr);
            } else {
              setPaymentRequest(null);
            }
            setIsStripeApplePaySupported(canUseApplePay);
            setIsStripeGooglePaySupported(canUseGooglePay);
          }
        })
        .catch((err) => {
          console.error("Error checking payment methods:", err);
          setPaymentRequest(null);
          setIsStripeApplePaySupported(false);
          setIsStripeGooglePaySupported(false);
        });
    } catch (error) {
      console.error("Error creating payment request:", error);
      setPaymentRequest(null);
      setIsStripeApplePaySupported(false);
      setIsStripeGooglePaySupported(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStripePayment, stripe]);

  // Effect B — keep the existing paymentRequest in sync with the current total.
  // Calls paymentRequest.update() so the Google Pay / Apple Pay sheet always shows
  // the correct amount after tips or coupons change without recreating the object.
  useEffect(() => {
    if (!paymentRequest || !(charges?.balanceDue > 0)) return;

    try {
      paymentRequest.update({
        total: {
          label: business?.title || business?.companyName || "Order Payment",
          amount: Math.round(charges.balanceDue * 100),
        },
      });
    } catch (error) {
      console.error("Error updating payment request:", error);
    }
  }, [
    paymentRequest,
    charges?.balanceDue,
    charges?.tipInfo?.kitchen,
    charges?.tipInfo?.driver,
    business?.title,
    business?.companyName,
  ]);

  const confirmCardPayment = async (clientSecret) => {
    try {
      const cardElement = elements?.getElement(CardNumberElement);
      const responseConfirm = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });
      if (responseConfirm?.error) {
        setIsPaid(false);
        toast.error(
          responseConfirm?.error?.data?.message || "Something went wrong"
        );
        return;
      }
      localStorage.setItem(`${oId}-balanceDue`, charges?.balanceDue);
      setIsPaid(true);
    } catch (error) {
      setIsPaid(false);
      console.error("Error in confirmCardPayment:", error);
      toast.error(error?.message || "Something went wrong");
    }
  };

  const handleSubmitGooglePayOrApplePay = async (clientSecret) => {
    if (!paymentRequest) {
      console.error("Payment request not available");
      toast.error("Payment request not available. Please try again.");
      return;
    }

    try {
      paymentRequest.on("paymentmethod", async (ev) => {
        const { paymentIntent, error: confirmError } =
          await stripe.confirmCardPayment(
            clientSecret,
            { payment_method: ev.paymentMethod.id },
            { handleActions: false }
          );

        if (confirmError) {
          setIsPaid(false);
          ev.complete("fail");
          const errorMessage =
            confirmError?.message ||
            confirmError?.decline_code ||
            "Payment failed. Please try again.";
          toast.error(errorMessage);
        } else {
          ev.complete("success");

          if (paymentIntent.status === "requires_action") {
            const { paymentIntent: finalIntent, error } =
              await stripe.confirmCardPayment(clientSecret, {
                payment_method: ev.paymentMethod.id,
              });
            if (error) {
              setIsPaid(false);
              const errorMessage =
                error?.message ||
                error?.decline_code ||
                "Payment failed. Please try again.";
              toast.error(errorMessage);
            } else {
              // Use the actual charged amount from Stripe as the source of truth
              if (finalIntent?.amount) {
                localStorage.setItem(`${oId}-balanceDue`, finalIntent.amount / 100);
              }
              setIsPaid(true);
            }
          } else {
            // Use the actual charged amount from Stripe as the source of truth
            if (paymentIntent?.amount) {
              localStorage.setItem(`${oId}-balanceDue`, paymentIntent.amount / 100);
            }
            setIsPaid(true);
          }
        }
      });
    } catch (error) {
      console.error("Error in handleSubmitGooglePayOrApplePay:", error);
      setIsPaid(false);
      const errorMessage =
        error?.message ||
        error?.data?.message ||
        "Payment failed. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleSubmitStripe = async (
    values = {},
    isGooglePayOrApplePay = false
  ) => {
    try {
      setIsLoading(true);
      // For Google Pay/Apple Pay, we don't need form values
      const requestData = {
        orderId: oId,
        tipInfo: {
          kitchen:
            (Number(charges?.tipInfo?.kitchen) || 0) + Number(paidTip || 0),
          driver: Number(charges?.tipInfo?.driver) || 0,
        },
        customerInfo: {
          email: values?.email || "",
          phoneNumber: values?.phone || "",
          firstName: values?.firstName || "",
          lastName: values?.lastName || "",
        },
      };

      const response = await applyPaymentCard({ requestData });

      if (response?.error) {
        setIsPaid(false);
        const errorMessage =
          response?.error?.data?.message ||
          "Payment setup failed. Please try again.";
        toast.error(errorMessage);
        return;
      }

      const clientSecret = response?.data?.client_secret;

      if (!clientSecret) {
        setIsPaid(false);
        toast.error("Payment setup failed. Please try again.");
        return;
      }

      if (clientSecret && isStripePayment && !isGooglePayOrApplePay) {
        await confirmCardPayment(clientSecret);
        return;
      }

      if (
        clientSecret &&
        (isStripeGooglePaySupported || isStripeApplePaySupported) &&
        paymentRequest &&
        isGooglePayOrApplePay
      ) {
        await handleSubmitGooglePayOrApplePay(clientSecret);
        return;
      }
    } catch (error) {
      console.error("Error in handleSubmitStripe:", error);
      setIsPaid(false);
      const errorMessage =
        error?.message ||
        error?.data?.message ||
        "Payment failed. Please try again.";
      toast.error(errorMessage);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1500);
    }
  };

  return {
    userInfo,
    handleSubmitStripe,
    isStripeApplePaySupported,
    isStripeGooglePaySupported,
    paymentRequest,
    isStripePayment,
    isLoading,
  };
};

export default useStripePay;
